import { Building2 } from "lucide-react";
import { Checkbox } from "@/frontend/components/ui/checkbox";
import { logoUrl } from "@/frontend/utils/shared";
import { formatFacilityDate, isFacilityActionable } from "@/frontend/utils/delete-patient-data";
import type { Facility, FacilityOrganization } from "@/frontend/types/delete-patient-data";
import type { DeletePatientDataListOut } from "@/server/schema/delete-patient-data";

type Props = {
  organizations: DeletePatientDataListOut["organizations"];
  /** Account ids currently ticked. */
  selected: ReadonlySet<string>;
  /** True while a confirmed action is running, which freezes the ticks. */
  pending: boolean;
  onToggleFacility: (accountId: string) => void;
  /** Ticks or clears every actionable facility in one workspace. */
  onToggleOrganization: (organization: FacilityOrganization) => void;
};

/**
 * The status line under a facility name, where deleted beats revoked beats live.
 * A confirmed delete already took the records off every surface, so the badge
 * says "Deleted" rather than describing a future event, and the erase date goes
 * on the meta line while the rows are still inside the purge wait.
 */
function StatusBadge({ facility }: { facility: Facility }) {
  if (facility.deletionScheduledAt) {
    return (
      <span className="inline-flex items-center rounded-full border border-destructive/40 bg-destructive/10 px-2 py-0.5 text-meta font-medium text-destructive">
        Deleted
      </span>
    );
  }
  if (facility.revoked) {
    return (
      <span className="inline-flex items-center rounded-full border bg-muted px-2 py-0.5 text-meta font-medium text-muted-foreground">
        Revoked
      </span>
    );
  }
  return null;
}

/** One connected health system, as a selectable row. */
function FacilityRow({
  facility,
  checked,
  disabled,
  onToggle,
}: {
  facility: Facility;
  checked: boolean;
  disabled: boolean;
  onToggle: () => void;
}) {
  const id = `facility-${facility.accountId}`;

  return (
    <li>
      <label
        htmlFor={id}
        className="flex cursor-pointer items-center gap-3 p-3 transition-colors hover:bg-accent/40 has-[:disabled]:cursor-default has-[:disabled]:hover:bg-transparent"
      >
        <Checkbox id={id} checked={checked} disabled={disabled} onCheckedChange={onToggle} />

        {logoUrl(facility.logo) ? (
          <img
            src={logoUrl(facility.logo) ?? ""}
            alt=""
            className="size-9 shrink-0 rounded object-contain"
          />
        ) : (
          <span
            aria-hidden
            className="flex size-9 shrink-0 items-center justify-center rounded bg-muted text-caption font-medium text-muted-foreground"
          >
            {facility.sourceName.slice(0, 1).toUpperCase()}
          </span>
        )}

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="break-words text-body font-medium">{facility.sourceName}</span>
            <StatusBadge facility={facility} />
          </div>
          <p className="mt-0.5 text-meta text-muted-foreground">
            Connected {formatFacilityDate(facility.connectedAt)} · Last retrieved{" "}
            {formatFacilityDate(facility.lastPullAt)} · {facility.recordCount.toLocaleString()}{" "}
            {facility.recordCount === 1 ? "record" : "records"}
            {facility.deletionScheduledAt
              ? ` · Fully erased ${formatFacilityDate(facility.deletionScheduledAt)}`
              : ""}
          </p>
        </div>
      </label>
    </li>
  );
}

/**
 * One workspace's header, holding a tick for the whole workspace, its name and
 * its facility count.
 */
function OrganizationHeader({
  organization,
  selected,
  pending,
  onToggle,
}: {
  organization: FacilityOrganization;
  selected: ReadonlySet<string>;
  pending: boolean;
  onToggle: () => void;
}) {
  const actionable = organization.facilities.filter(isFacilityActionable);
  const ticked = actionable.filter((facility) => selected.has(facility.accountId)).length;
  const count = organization.facilities.length;
  const id = `workspace-${organization.organizationId}`;

  return (
    <header className="border-b">
      <label
        htmlFor={id}
        className="flex cursor-pointer items-center gap-3 px-3 py-2.5 transition-colors hover:bg-accent/40 has-[:disabled]:cursor-default has-[:disabled]:hover:bg-transparent"
      >
        <Checkbox
          id={id}
          // Partly ticked reads as neither on nor off, which is what it is.
          checked={ticked === 0 ? false : ticked === actionable.length ? true : "indeterminate"}
          disabled={pending || actionable.length === 0}
          onCheckedChange={onToggle}
        />
        <Building2 aria-hidden className="size-4 shrink-0 text-muted-foreground" />
        <h2 className="min-w-0 break-words text-body font-semibold">
          {organization.organizationName}
        </h2>
        <span className="shrink-0 text-meta text-muted-foreground">
          {count} {count === 1 ? "health system" : "health systems"}
        </span>
      </label>
    </header>
  );
}

/**
 * The signed-in half of `/delete-your-data`, listing every workspace holding
 * this patient's data and every health system inside it. One portal sign-in
 * produces the whole list, including workspaces the patient never heard of.
 * Rows are ticked, and the two actions live once at the foot.
 */
export function FacilityList({
  organizations,
  selected,
  pending,
  onToggleFacility,
  onToggleOrganization,
}: Props) {
  if (organizations.length === 0) {
    return (
      <div className="rounded-lg border p-6 text-center">
        <p className="text-body font-medium">Nothing is connected</p>
        <p className="mt-1 text-caption text-muted-foreground">
          We hold no health records linked to the portal you signed in with.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      {organizations.map((org) => (
        <section key={org.organizationId} className="overflow-hidden rounded-lg border">
          <OrganizationHeader
            organization={org}
            selected={selected}
            pending={pending}
            onToggle={() => onToggleOrganization(org)}
          />
          <ul className="divide-y">
            {org.facilities.map((facility) => (
              <FacilityRow
                key={facility.accountId}
                facility={facility}
                checked={selected.has(facility.accountId)}
                disabled={pending || !isFacilityActionable(facility)}
                onToggle={() => onToggleFacility(facility.accountId)}
              />
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
